import { localNow, serverNow, serverOffset } from "@/filters/DateAndTime";
import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import { Patient } from "@/store/modules/patient/types";
import { towerNamespace } from "@/store/modules/tower";
import { TowerAction } from "@/store/modules/tower/actions";
import { TOWER_REVIEW_SAVE } from "@/store/modules/tower/mutation_types";
import _ from "lodash";
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { newWindowEventListeners, TIME_ONLY_FORMAT } from "../shared/util";
import CheckOrDropdown from "./tower/CheckOrDropdown";
import RiskAssessmentCheckList from "./tower/RiskAssessmentCheckList";

type ContactTypes = "" | "phone" | "pager" | "email";

interface Contact {
  type: ContactTypes;
  sent: string;
  response: string;
}

@Component({
  components: { RiskAssessmentCheckList, CheckOrDropdown }
})
export default class review extends Vue {
  @Getter("current", patientNamespace)
  current!: Patient;

  @Getter("vars", patientNamespace)
  vars!: any;

  @Action(TOWER_REVIEW_SAVE, towerNamespace)
  [TOWER_REVIEW_SAVE]!: Function;

  editing = false;
  waiting = false;
  towerNote = "";
  patientHistory = "";
  summaryTextHasChanges = 0;
  contactSelectItems = [
    { value: "", text: "(Select How)" },
    { value: "phone", text: "Phone" },
    { value: "pager", text: "Pager" },
    { value: "email", text: "Email" }
  ];
  contacts: Array<Contact> = [this.createBlankContact()];

  riskValuesGrouped: Record<string, Record<string, any>> = {};

  createBlankContact(): Contact {
    return { type: "", sent: "", response: "" };
  }

  updateContacts(contact: Contact) {
    const now = this.getNow();
    contact.sent = now;
    if (contact.type === "phone") {
      contact.response = now;
    }
    if (this.contacts[this.contacts.length - 1] === contact) {
      this.contacts.push(this.createBlankContact());
    }
  }

  updateTime(contact: Contact) {
    if (contact.type !== "" && contact.response === "") {
      contact.response = this.getNow();
    }
  }

  private getNow() {
    return serverNow()
      .local()
      .format(TIME_ONLY_FORMAT);
  }

  @Watch("current.body", { immediate: true })
  handleBodyUpdate(currentBody: any) {
    if (currentBody) {
      // Only overrite if empty
      if (!this.patientHistory) {
        this.patientHistory = this.caseReview.patientHistory;
      }
      if (this.contacts.length === 1) {
        this.contacts = _.cloneDeep(this.caseReview.contact)
          .map((c: Contact) => {
            // convert null to empty string for form
            if (c.response === null) {
              c.response = "";
            }
            return c;
          })
          .concat(this.contacts);
      }
      if (this.towerNote === "") {
        this.towerNote = this.current.body.current.TowerNote.text;
      }
    }
  }

  get augmentedCaseReviews() {
    const assess = _.cloneDeep(this.caseReview.assess);
    Object.keys(assess).forEach((key: any) => {
      const value = assess[key];

      Object.keys(value).forEach((nestedKey: string) => {
        const nestedValue = value[nestedKey];

        // Other's don't have valid index values and are manual entry
        // need to special case them and return
        if (nestedKey.startsWith("Other")) {
          if (key === "CompReco") {
            nestedValue.category = "Other";
            nestedValue.format = "Other";
          } else {
            nestedValue.category = "";
          }
          nestedValue.id = nestedKey;
          return;
        }

        // Otherwise look up id, category and options from caseReviewRules
        const assess = this.caseReviewRules.assess;
        const indexRef = assess[nestedValue.index];
        nestedValue.id = indexRef.id;
        nestedValue.category = indexRef.category;
        nestedValue.options = this.groupedCaseReviewRules[indexRef.category][
          indexRef.id
        ].map((value: any, index: number) => {
          return { text: value.text, value: index, verbose: value.verbose };
        });
      });

      assess[key] = _.groupBy(value, "category");
    });
    return assess;
  }

  get groupedCaseReviewRules() {
    const categoryGroup = _.groupBy(
      this.caseReviewRules.assess,
      "category"
    ) as any;

    Object.keys(categoryGroup).forEach(key => {
      categoryGroup[key] = _.groupBy(categoryGroup[key], "id");
    });

    return categoryGroup;
  }

  get caseReview() {
    return this.current.body.caseReview;
  }
  get caseReviewRules() {
    return JSON.parse(this.vars.logicRules.RuleCaseReview);
  }

  // If false, not part of the study
  get treatmentPatient(): boolean {
    return this.current.census.treatment === 1;
  }

  get treatmentText() {
    const census = this.current.census;
    if (census.age > 0 && census.age < 18) {
      return "Patient Less Than 18 Years Old";
    } else if (this.treatmentPatient) {
      return "Treatment Patient: Contact Providers";
    } else {
      return "Control Patient: Do Not Contact Providers";
    }
  }

  // Event from child components to update riskValues
  riskUpdate(assessments: Array<any>, group: string) {
    this.riskValuesGrouped[group] = assessments;
    // Rebuild summary text every time we get a riskUpdate event
    this.summaryTextHasChanges++;
  }

  async edit() {
    this.waiting = true;
    try {
      const roomSys = this.$route.params.room;
      const response = await this[TOWER_REVIEW_SAVE]({
        item: { claim: 1 },
        action: TowerAction.EDIT_CASE_REVIEW,
        roomSys
      });

      if (response.status === "good") {
        this.editing = true;
      }
    } finally {
      this.waiting = false;
    }
  }

  async saveProgress() {
    await this.save({ complete: 0 });
  }

  mounted() {
    const ref = this;
    newWindowEventListeners(ref, "review", this.$route.params.room);
  }

  async complete() {
    await this.save({ complete: 1 });
    this.editing = false;
  }

  @Action("hidePopup", navigationNamespace)
  hidePopup!: Function;

  async close() {
    await this.complete();
    this.hidePopup();
  }

  get summaryText() {
    if (!this.summaryTextHasChanges) {
      return [];
    }

    const textValues: Array<Array<any>> = [];

    const configs = [
      { text: "MAJOR EVENT, HIGH RISK: ", group: "CompMajor", value: 2 },
      { text: "MINOR EVENT, HIGH RISK: ", group: "CompMinor", value: 2 },
      { text: "MAJOR EVENT, LOW RISK: ", group: "CompMajor", value: 1 },
      { text: "MINOR EVENT, LOW RISK: ", group: "CompMinor", value: 1 }
    ];

    configs.forEach((config: any) => {
      const assessments = this.riskValuesGrouped[config.group];
      const filtered = assessments.filter((assessment: any) => {
        if (assessment.other) {
          return assessment.text && assessment.value === config.value;
        } else {
          return assessment.value === config.value;
        }
      });

      // build string and push only if there are some filtered results
      if (filtered.length === 0) {
        return;
      }
      textValues.push(
        config.text +
          filtered
            .map((f: any, i: number) => {
              const map = this.mappedToKeyCaseReviews[config.group];
              const item = map[f.key];
              let text = "";
              if (f.text && f.other) {
                text = f.text;
              } else if (!f.other) {
                text = this.createText(item);
              }
              return `(${i + 1}) ${text}`;
            })
            .join(" ")
      );
    });
    return textValues;
  }

  createText(item: any, value = 0) {
    return item.options[value].verbose;
  }

  get mappedToKeyCaseReviews() {
    const mapped: Record<string, any> = {};
    Object.keys(this.augmentedCaseReviews).forEach(group => {
      Object.keys(this.augmentedCaseReviews[group]).forEach(subkey => {
        this.augmentedCaseReviews[group][subkey].forEach((subItem: any) => {
          if (!mapped[group]) {
            mapped[group] = {};
          }
          mapped[group][subItem.id] = subItem;
        });
      });
    });
    return mapped;
  }

  get recSummaryText() {
    if (!this.summaryTextHasChanges) {
      return [];
    }
    const mapped = this.mappedToKeyCaseReviews.CompReco;

    const recGroups = [
      "CompReco.Heart",
      "CompReco.Blood",
      "CompReco.PostOp",
      "CompReco.Diabetes",
      "CompReco.Other",
      "CompReco.PONVProphylaxis"
    ];
    const recLines: string[] = [];
    recGroups.forEach(key => {
      const rec = this.riskValuesGrouped[key];
      const filtered = rec.filter((rec: any) => {
        if (rec.other) {
          return rec.text && rec.value !== 0;
        } else {
          return rec.value !== 0;
        }
      });
      if (filtered.length !== 0) {
        filtered.forEach((f: any) => {
          const inputDetail = mapped[f.key];
          if (f.text && f.other) {
            recLines.push(f.text);
          } else {
            // default to 0 for checkboxes, their value is 1 when checked
            let value = 0;

            if (inputDetail.format === 3) {
              // Drop down, use selected index
              value = f.value;
            }
            recLines.push(this.createText(inputDetail, value));
          }
        });
      }
    });
    if (recLines.length) {
      recLines.forEach((line, i) => {
        recLines[i] = `(${i + 1}) ${line}`;
      });
      recLines.unshift("RECOMMENDATIONS");
    }
    return recLines;
  }

  async save(dataToSave: Record<string, any> = {}) {
    this.waiting = true;
    const riskValues: Record<string, any> = {};

    // flatten to just key value pairs
    Object.values(this.riskValuesGrouped).forEach(group => {
      group.forEach((assessment: any) => {
        riskValues[assessment.key] = assessment.value;
        // Other requires value + text entered
        if (assessment.other) {
          riskValues[assessment.key + ".Text"] = assessment.text;
        }
      });
    });

    Object.keys(riskValues).forEach(key => {
      if (riskValues[key]) {
        dataToSave[key] = riskValues[key];
      }
    });

    dataToSave.patientHistory = this.patientHistory;
    let index = 1;
    this.contacts.forEach(contact => {
      if (contact.type) {
        dataToSave["contactType." + index] = contact.type;

        // Break up hh:mm into hour and minute, updating today (from serverNow())
        // to use local client time
        const sentParts = contact.sent.split(":");

        // Shift the time to server time until server starts sending
        // with date time offset
        const diff = serverOffset() + new Date().getTimezoneOffset();

        const sent = contact.sent
          ? localNow()
              .set("hour", parseInt(sentParts[0], 10))
              .set("minute", parseInt(sentParts[1]))
              .add(diff, "minute")
              .format()
          : "";

        const responseParts = contact.response.split(":");
        const response = contact.response
          ? localNow()
              .set("hour", parseInt(responseParts[0], 10))
              .set("minute", parseInt(responseParts[1]))
              .add(diff, "minute")
              .format()
          : "";
        dataToSave["contactTime." + index] = sent;
        dataToSave["responseTime." + index] = response;
        index++;
      }
    });

    dataToSave.towerNote = this.towerNote;

    try {
      const roomSys = this.$route.params.room;
      await this[TOWER_REVIEW_SAVE]({
        item: dataToSave,
        action: TowerAction.SAVE_CASE_REVIEW,
        roomSys
      });
    } finally {
      this.waiting = false;
    }
  }

  timeCheck(input: string) {
    return (
      /^([01]\d|2[0-3]):([0-5]\d)$/.test(input) ||
      "24 hour time must be in HH:MM format"
    );
  }

  async copySummary() {
    const textToCopy = [
      this.patientHistory,
      this.summaryText.join("\n\r"),
      this.recSummaryText.join("\n\r")
    ].join("\n\r");

    await (navigator as any).clipboard.writeText(textToCopy);
    await this.save({ complete: 2 });
    this.editing = false;
  }
}
